import type Keycloak from 'keycloak-js';
import {
  createContext,
  FC,
  PropsWithChildren,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  consumeKeycloakReturnTo,
  ensureKeycloakInitialized,
  getActiveKeycloakClientConfig,
  getDefaultKeycloakClientConfig,
  getKeycloakClient,
  getKeycloakRedirectUri,
  isKeycloakEnabled,
  KeycloakClientConfig,
  saveKeycloakReturnTo,
  setActiveAuthProvider,
} from './keycloakClient';

// Explicit common-claim fields (matching @auth0/auth0-spa-js's User shape
// closely enough for call sites like `user?.sub`/`user?.email` to keep
// their existing, precise types instead of widening to `any` - which is
// what happens if this were just `Record<string, any>` and TS has to
// union it with Auth0's User type).
export interface KeycloakUser {
  sub: string;
  email?: string;
  email_verified?: boolean;
  given_name?: string;
  family_name?: string;
  name?: string;
  picture?: string;
  [key: string]: any;
}

export interface KeycloakAuthContextValue {
  isAuthenticated: boolean;
  isLoading: boolean;
  // Set when the SDK fails to initialize on mount for a returning Keycloak
  // user (Keycloak unreachable, init timed out, or the login callback in
  // the URL was rejected) - App.tsx renders it through the same auth-error
  // path as Auth0Provider's `error`. Not set for later token-refresh
  // failures: those surface as getAccessTokenSilently() rejections, which
  // App.tsx's request interceptor already turns into a re-login.
  error: Error | undefined;
  user: KeycloakUser | undefined;
  loginWithRedirect: (options?: {
    appState?: { returnTo?: string };
    loginHint?: string;
    // Which Keycloak client to log in through - the `keycloak` config
    // /api/auth/resolve-provider answered for the user's organization (its
    // own instance, or the deploy's default). Omitted, the client of the
    // current session (or the default) is used.
    client?: KeycloakClientConfig;
  }) => Promise<void>;
  logout: (options?: { returnTo?: string; localOnly?: boolean }) => void;
  // Accepts (and ignores) an options arg for call-site parity with Auth0's
  // getAccessTokenSilently(options) - Keycloak's token scope/audience is
  // fixed by the client config, not per-call.
  getAccessTokenSilently: (options?: any) => Promise<string>;
  getIdTokenClaims: () => Promise<Record<string, any> | undefined>;
}

const noAuth: KeycloakAuthContextValue = {
  isAuthenticated: false,
  isLoading: false,
  error: undefined,
  user: undefined,
  loginWithRedirect: async () => {
    throw new Error('Keycloak is not the active auth provider.');
  },
  logout: () => {},
  getAccessTokenSilently: async () => {
    throw new Error('Keycloak is not the active auth provider.');
  },
  getIdTokenClaims: async () => undefined,
};

export const KeycloakAuthContext =
  createContext<KeycloakAuthContextValue>(noAuth);

const buildUserFromTokenParsed = (
  tokenParsed: Record<string, any> | undefined
): KeycloakUser | undefined => {
  if (!tokenParsed?.sub) {
    return undefined;
  }
  return {
    ...tokenParsed,
    // Prefix so this can never collide with an Auth0 sub (which is always
    // "<connection>|<id>" already) - mirrors the backend's
    // central.<organization>.<sub> username (api.authentication.keycloakbackend),
    // where one Keycloak account is one User per organization.
    sub: `central.${getOrganizationClaimSlug(tokenParsed)}.${tokenParsed.sub}`,
  };
};

// The organization the token was issued for. Keycloak's organization
// membership mapper mints the claim either as a list of aliases or as an
// object keyed by alias, depending on its configuration.
export const getOrganizationClaimSlug = (
  tokenParsed: Record<string, any>
): string => {
  const claim = tokenParsed.organization;
  if (typeof claim === 'string') {
    return claim;
  }
  if (Array.isArray(claim)) {
    return claim[0] ?? '';
  }
  if (claim && typeof claim === 'object') {
    return Object.keys(claim)[0] ?? '';
  }
  return '';
};

// keycloak-js doesn't always reject with an Error: a login callback that
// comes back with `error=...` in the URL is thrown as a bare
// {error, error_description} object. Normalize so App.tsx can rely on
// `error.message` the way it does for Auth0's errors.
const toError = (e: unknown): Error => {
  if (e instanceof Error) {
    return e;
  }
  if (e && typeof e === 'object') {
    const { error, error_description } = e as {
      error?: string;
      error_description?: string;
    };
    if (error || error_description) {
      return new Error(error_description || error);
    }
  }
  return new Error(String(e));
};

// The client to log in through when loginWithRedirect() is called without
// one: whatever the current session already uses, else the deploy's
// default.
const resolveLoginConfig = (
  client: KeycloakClientConfig | undefined,
  active: KeycloakClientConfig | undefined
): KeycloakClientConfig => {
  const config = client ?? active ?? getDefaultKeycloakClientConfig();
  if (!config) {
    throw new Error('No Keycloak client is configured.');
  }
  return config;
};

// Mounted app-wide (see AuthProvider.tsx), but only actually initializes
// the Keycloak SDK - and thus only ever makes a network call - when the
// user's last login was via Keycloak (see keycloakClient.ts). For the
// common Auth0-only case this is a static, inert "not authenticated" value.
//
// Which client is in play: for a returning user it's the one persisted
// with the active-provider marker (whose issuer the token's own `iss` is
// checked against after init); for a first-time login it's the one
// loginWithRedirect() is handed by the identifier-first page.
export const KeycloakAuthContextProvider: FC<
  PropsWithChildren<{
    // Called once after a login redirect, with where the user was headed.
    onRedirectCallback?: (appState?: { returnTo?: string }) => void;
  }>
> = ({ children, onRedirectCallback }) => {
  // The client of the session being resumed on mount, if any - fixed for
  // the life of this mount, like the SDK instance it maps to.
  const [activeConfig] = useState<KeycloakClientConfig | undefined>(() =>
    isKeycloakEnabled() ? getActiveKeycloakClientConfig() : undefined
  );
  // The SDK instance the value below talks to: the resumed session's, or
  // the one loginWithRedirect() picked. Not state - it never re-renders
  // anything by itself, the sync callbacks it's wired with do.
  const clientRef = useRef<{
    config: KeycloakClientConfig;
    client: Keycloak;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(() => !!activeConfig);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<KeycloakUser | undefined>(undefined);
  const [error, setError] = useState<Error | undefined>(undefined);

  useEffect(() => {
    if (!activeConfig) {
      return;
    }
    const client = getKeycloakClient(activeConfig);
    clientRef.current = { config: activeConfig, client };

    const syncState = () => {
      setIsAuthenticated(client.authenticated ?? false);
      setUser(buildUserFromTokenParsed(client.tokenParsed));
    };

    client.onAuthSuccess = syncState;
    client.onAuthRefreshSuccess = syncState;
    client.onAuthLogout = () => {
      setActiveAuthProvider('auth0');
      setIsAuthenticated(false);
      setUser(undefined);
    };
    client.onTokenExpired = () => {
      client.updateToken(5).catch(() => {
        setIsAuthenticated(false);
        setUser(undefined);
      });
    };

    ensureKeycloakInitialized(activeConfig)
      .then(() => {
        // The token's issuer is the authority on which client a session
        // belongs to - the stored marker is only a hint for picking the
        // instance to init. If they disagree (e.g. a realm re-pointed at
        // another URL), treat it like any other init failure rather than
        // running the app on a token verified against the wrong realm.
        const issuer = client.tokenParsed?.iss;
        if (client.authenticated && issuer && issuer !== activeConfig.issuer) {
          throw new Error(
            `Keycloak token issuer ${issuer} does not match the configured client ${activeConfig.issuer}.`
          );
        }
        syncState();
        // Coming back from a login: init() has just consumed Keycloak's
        // answer, so go to where the user was headed (the counterpart of
        // Auth0Provider's onRedirectCallback). Done before isLoading drops,
        // so the app never renders the bare redirect URL first.
        const returnTo = consumeKeycloakReturnTo();
        if (returnTo && client.authenticated) {
          onRedirectCallback?.({ returnTo });
        }
      })
      .catch((e) => {
        // A destination saved for a login that then failed must not
        // resurface on some later, unrelated one.
        consumeKeycloakReturnTo();
        const initError = toError(e);
        console.error('Keycloak initialization failed:', initError);
        // Deliberately leaves the active provider on Keycloak: which
        // provider a user logs in with is decided per organization by the
        // backend (/api/auth/resolve-provider), so a Keycloak user has no
        // Auth0 fallback to offer - the honest outcome is App.tsx's error
        // page (and another attempt on reload), not the login picker.
        setIsAuthenticated(false);
        setUser(undefined);
        setError(initError);
      })
      .finally(() => setIsLoading(false));
    // onRedirectCallback is stable (a useCallback in AuthProvider.tsx), so
    // this still runs once; were it ever to change, a re-run is harmless -
    // ensureKeycloakInitialized() is memoized and the saved destination is
    // only handed out once.
  }, [activeConfig, onRedirectCallback]);

  const value = useMemo<KeycloakAuthContextValue>(
    () => ({
      isAuthenticated,
      isLoading,
      error,
      user,
      loginWithRedirect: async (options) => {
        if (!isKeycloakEnabled()) {
          throw new Error('Keycloak login is disabled.');
        }
        const config = resolveLoginConfig(options?.client, activeConfig);
        setActiveAuthProvider('keycloak', config);
        const client = getKeycloakClient(config);
        clientRef.current = { config, client };
        // Must complete before login() - keycloak-js relies on init()
        // having set up internal state (endpoints, PKCE, etc.); calling
        // login() beforehand silently produces a malformed redirect_uri
        // instead of failing loudly.
        await ensureKeycloakInitialized(config);
        // The destination (any path, query and hash) waits in
        // sessionStorage and is restored on the way back - see the mount
        // effect above. redirect_uri itself is always the same bare URL.
        const returnTo = options?.appState?.returnTo;
        saveKeycloakReturnTo(
          typeof returnTo === 'string' ? returnTo : undefined
        );
        return client.login({
          redirectUri: getKeycloakRedirectUri(),
          loginHint: options?.loginHint,
        });
      },
      logout: async (options) => {
        setActiveAuthProvider('auth0');

        const current = clientRef.current;
        if (!current) {
          // Nothing was ever initialized on this page (e.g. the marker was
          // stale) - there is no Keycloak session to end.
          return;
        }

        if (options?.localOnly) {
          // Matches Auth0's localOnly semantics: clear the local session
          // without redirecting to the IdP's logout endpoint at all - so
          // (unlike a real logout redirect) returnTo is never used here,
          // and doesn't need to be a URL-safe string. Call sites like
          // ForcedAuthModal rely on this: they pass a router `location`
          // object as returnTo specifically because Auth0 ignores it
          // under localOnly.
          current.client.clearToken();
          return;
        }

        await ensureKeycloakInitialized(current.config);
        const returnTo = options?.returnTo;
        current.client.logout({
          redirectUri:
            typeof returnTo === 'string' ? returnTo : window.location.origin,
        });
      },
      getAccessTokenSilently: async () => {
        const current = clientRef.current;
        if (!current) {
          throw new Error('Keycloak is not the active auth provider.');
        }
        await ensureKeycloakInitialized(current.config);
        await current.client.updateToken(5);
        if (!current.client.token) {
          throw new Error('No Keycloak token available.');
        }
        return current.client.token;
      },
      getIdTokenClaims: async () => clientRef.current?.client.idTokenParsed,
    }),
    [isAuthenticated, isLoading, user, error, activeConfig]
  );

  return (
    <KeycloakAuthContext.Provider value={value}>
      {children}
    </KeycloakAuthContext.Provider>
  );
};
